import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import EstoreChrome from "../components/EstoreChrome";
import { brand } from "../data/estoreContent";
import { ESTORE_ROUTES } from "../data/estorePaths";
import { useEstoreCart } from "../hooks/useEstoreCart";
import { clearEstoreCart } from "../utils/estoreCart";

const steps = ["Shipping", "Review"];

function EstoreCheckout() {
  const navigate = useNavigate();
  const { lines, subtotal } = useEstoreCart();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", city: "", pin: "" });

  if (lines.length === 0) {
    return (
      <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
        <EstoreChrome title="Checkout" />
        <Container sx={{ py: 8, textAlign: "center" }}>
          <Typography sx={{ mb: 2 }}>Your bag is empty.</Typography>
          <Button variant="contained" onClick={() => navigate(ESTORE_ROUTES.shop)}>Go to shop</Button>
        </Container>
      </Box>
    );
  }

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = () => {
    clearEstoreCart();
    navigate(ESTORE_ROUTES.home);
  };

  return (
    <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
      <EstoreChrome title="Checkout" showBack />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stepper activeStep={step} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Shipping</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full name" value={form.name} onChange={onField("name")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" value={form.email} onChange={onField("email")} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Phone" value={form.phone} onChange={onField("phone")} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" value={form.line1} onChange={onField("line1")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" value={form.city} onChange={onField("city")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PIN" value={form.pin} onChange={onField("pin")} />
              </Grid>
            </Grid>
            <Button variant="contained" sx={{ mt: 3, borderRadius: 999, textTransform: "none", fontWeight: 800 }} onClick={() => setStep(1)}>
              Continue
            </Button>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Review</Typography>
            <Card elevation={0} sx={{ border: `1px solid ${brand.stroke}`, borderRadius: 3, p: 2, mb: 2 }}>
              {lines.map((l) => (
                <Box key={l.productId} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                  <Typography>{l.title} × {l.qty}</Typography>
                  <Typography fontWeight={700}>₹{l.unitPrice * l.qty}</Typography>
                </Box>
              ))}
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 2, borderTop: `1px solid ${brand.stroke}` }}>
                <Typography fontWeight={800}>Total</Typography>
                <Typography fontWeight={800}>₹{subtotal}</Typography>
              </Box>
            </Card>
            <Typography variant="body2" sx={{ color: brand.muted, mb: 2 }}>
              Demo checkout — no payment is processed.
            </Typography>
            <Button variant="outlined" sx={{ mr: 1, borderRadius: 999, textTransform: "none" }} onClick={() => setStep(0)}>Back</Button>
            <Button variant="contained" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 800 }} onClick={placeOrder}>
              Place order
            </Button>
          </motion.div>
        )}
      </Container>
    </Box>
  );
}

export default EstoreCheckout;
